import React, { useState, useEffect } from 'react';
import { H5, H6 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col, Button, Card, CardBody } from 'reactstrap';
import { toast } from 'react-toastify';
import { uploadFile, fetchFormData } from '../../../../Api/ApiMethods';
import FilesComponent from '../Component/FilesComponent';

const ExpertForm = ({ formData, errors, setFormData, handleInputChange, handleAddWorkFile, handleRemoveWorkFile, workFileTypes, experts, handleExpertChange, insuranceCompanies, callFormState, fieldPermissions, canEdit = true, accidentFileId }) => {
    const [newFile, setNewFile] = useState({
        fileType: [],
        fileTypeExpenses: {}, // Her dosya türü ID'si için ayrı tutar: { typeId: expense }
        withReport: true,
        withFault: false,
        withoutReport: false,
        numberOfDays: '',
        expensePerDay: ''
    });

    const [selectedFileTypes, setSelectedFileTypes] = useState([]);

    // State for handling expert report and insurance application files for each accident file
    const [expertReportFiles, setExpertReportFiles] = useState({}); // Key: accidentFileId, Value: files array
    const [insuranceApplicationFiles, setInsuranceApplicationFiles] = useState({}); // Key: accidentFileId, Value: files array

    // Helper function to check field access based on permissions
    const getFieldAccess = (fieldName) => {
        // If canEdit is false, all fields should be disabled
        if (!canEdit) {
            return {
                disabled: true,
                required: false,
            };
        }

        // Ensure fieldPermissions is an array before calling .find()
        const safeFieldPermissions = Array.isArray(fieldPermissions) ? fieldPermissions : [];
        // Case-insensitive comparison for robustness
        const permission = safeFieldPermissions.find(p => p.fieldName.toLowerCase() === fieldName.toLowerCase());
        // Return 'disabled' directly for easier use
        return {
            // Set disabled to true if not editable (ReadOnly) or not found (default to not editable for safety)
            disabled: permission ? permission.access !== 'Editable' : true,
            required: permission ? permission.required : false,
        };
    };

    // Define field access (verify API field names!)
    // Permissions for adding/removing work files might need specific API fields
    const addWorkFileAccess = getFieldAccess('accidentfile.filestobeworked.add'); // Example field name
    const removeWorkFileAccess = getFieldAccess('accidentfile.filestobeworked.remove'); // Example field name
    // Permissions for individual fields in the 'New File' section
    const fileTypeAccess = getFieldAccess('accidentfile.filestobeworked.filetype');
    const fileExpenseAccess = getFieldAccess('accidentfile.filestobeworked.fileexpense');
    const lorExpenseAccess = getFieldAccess('accidentfile.filestobeworked.lorexpense'); // Or numberOfDays/expensePerDay
    const numberOfDaysAccess = getFieldAccess('accidentfile.filestobeworked.numberofdays');
    const expensePerDayAccess = getFieldAccess('accidentfile.filestobeworked.expenseperday');
    const withReportAccess = getFieldAccess('accidentfile.filestobeworked.withreport');
    const withFaultAccess = getFieldAccess('accidentfile.filestobeworked.withfault');
    const withoutReportAccess = getFieldAccess('accidentfile.filestobeworked.withoutreport');
    const isOpenDamageFileAccess = getFieldAccess('accidentfile.isthereopendamagefile');
    const whoseInsuranceAccess = getFieldAccess('accidentfile.whoseinsurance');
    const ourFaultRateAccess = getFieldAccess('accident.ourfaultrate');
    // --- YENİ: Sigorta Alanları için İzin Kontrolleri ---
    const clientTrafficAccess = getFieldAccess('licenseplates.trafficinsurancepolicy');
    const clientCascoAccess = getFieldAccess('licenseplates.cascoinsurancepolicy');
    const counterpartyPlateAccess = getFieldAccess('accidents.counterpartylicenseplatenumber'); // Added for consistency
    const counterpartyFaultRateAccess = getFieldAccess('accident.counterpartyfaultrate'); // Added for consistency
    const cpTrafficAccess = getFieldAccess('accidents.counterpartytrafficinsurancepolicy');
    const cpCascoAccess = getFieldAccess('accidents.counterpartycascoinsurancepolicy');
    // --- YENİ: Başlangıç Tarihi İzinleri ---
    const clientTrafficStartAccess = getFieldAccess('licenseplates.trafficinsurancestart');
    const clientCascoStartAccess = getFieldAccess('licenseplates.cascoinsurancestart');
    const cpTrafficStartAccess = getFieldAccess('accidents.counterpartytrafficinsurancestart');
    const cpCascoStartAccess = getFieldAccess('accidents.counterpartycascoinsurancestart');
    // --- BİTTİ: Başlangıç Tarihi İzinleri ---

    const handleChange = (field, value) => {
        setNewFile((prev) => {
            const updatedFile = { ...prev, [field]: value };

            // Hak Mahrumiyeti seçiliyse ve gün sayısı veya gün başı fiyat değişirse toplam tutarı hesapla
            if (updatedFile.fileType.some(typeId => isLorFileType(typeId)) && (field === 'numberOfDays' || field === 'expensePerDay')) {
                const days = parseInt(field === 'numberOfDays' ? value : updatedFile.numberOfDays) || 0;
                const ratePerDay = parseInt(field === 'expensePerDay' ? value : updatedFile.expensePerDay) || 0;

                const totalExpense = days * ratePerDay;
                updatedFile.fileExpense = totalExpense.toString();
            }

            return updatedFile;
        });
    };

    const isLorFileType = (fileTypeIdOrObj) => {
        if (!fileTypeIdOrObj) return false;

        // If it's an object with title property
        if (typeof fileTypeIdOrObj === 'object' && fileTypeIdOrObj.title) {
            const title = fileTypeIdOrObj.title.toLowerCase();
            return title.includes('hak mahrumiyeti') ||
                title.includes('lor') ||
                title.includes('mahrumiyeti');
        }

        // If it's an ID, look up in workFileTypes
        if (typeof fileTypeIdOrObj === 'string' || typeof fileTypeIdOrObj === 'number') {
            const fileTypeObj = workFileTypes.find(type => type.id === fileTypeIdOrObj.toString());
            if (fileTypeObj && fileTypeObj.title) {
                const title = fileTypeObj.title.toLowerCase();
                return title.includes('hak mahrumiyeti') ||
                    title.includes('lor') ||
                    title.includes('mahrumiyeti');
            }
        }

        return false;
    };

    const handleFileTypeChange = (fileTypeId, checked) => {
        let newSelectedTypes = [...newFile.fileType];

        if (checked) {
            if (newSelectedTypes.length >= 2) {
                toast.warning('En fazla 2 dosya türü seçebilirsiniz.');
                return;
            }

            if (isLorFileType(fileTypeId) && formData.willLorBeWorked === "no") {
                toast.warning('Hak Mahrumiyeti çalışılmayacak olarak işaretlendiği için bu dosya türü seçilemez.');
                return;
            }

            newSelectedTypes.push(fileTypeId);
        } else {
            newSelectedTypes = newSelectedTypes.filter(id => id !== fileTypeId);
            // Dosya türü kaldırıldığında o türe ait tutarı da temizle
            setNewFile(prev => {
                const newExpenses = { ...prev.fileTypeExpenses };
                delete newExpenses[fileTypeId];
                return {
                    ...prev,
                    fileTypeExpenses: newExpenses
                };
            });
        }

        setNewFile(prev => ({
            ...prev,
            fileType: newSelectedTypes
        }));

        const selectedTitles = newSelectedTypes.map(id => {
            const fileType = workFileTypes.find(type => type.id === id);
            return fileType ? fileType.title : '';
        });

        setSelectedFileTypes(selectedTitles);
    };

    // Her dosya türü için ayrı tutar girişi
    const handleFileTypeExpenseChange = (fileTypeId, value) => {
        setNewFile(prev => ({
            ...prev,
            fileTypeExpenses: {
                ...prev.fileTypeExpenses,
                [fileTypeId]: value
            }
        }));
    };

    const handleCheckboxChange = (field) => {
        if (field === 'withReport' || field === 'withoutReport') {
            if (field === 'withReport') {
                if (newFile.withReport && !newFile.withoutReport) {
                    return;
                }

                setNewFile((prev) => ({
                    ...prev,
                    withReport: true,
                    withoutReport: false
                }));
            }
            else if (field === 'withoutReport') {
                if (newFile.withoutReport && !newFile.withReport) {
                    return;
                }

                setNewFile((prev) => ({
                    ...prev,
                    withoutReport: true,
                    withReport: false
                }));
            }
        } else {
            setNewFile((prev) => ({ ...prev, [field]: !prev[field] }));
        }
    };    // Helper function to find file type ID by name
    const getFileTypeIdByName = (name) => {
        console.log('Looking for file type with name:', name);
        // This function is now used only for legacy purposes if needed
        return null;
    };

    // FilesComponent handlers for expert reports and insurance applications
    const handleExpertReportFilesChange = (accidentFileId, files) => {
        setExpertReportFiles(prev => ({
            ...prev,
            [accidentFileId]: files
        }));
    };

    const handleInsuranceApplicationFilesChange = (accidentFileId, files) => {
        setInsuranceApplicationFiles(prev => ({
            ...prev,
            [accidentFileId]: files
        }));
    };

    // --- BİTTİ: Sigorta Alanları için İzin Kontrolleri ---

    const handleAdd = () => {
        // Check permission before adding
        if (addWorkFileAccess.disabled) {
            toast.warning('Dosya ekleme yetkiniz bulunmamaktadır.');
            return;
        }
        if (newFile.fileType.length === 0) {
            toast.warning('Lütfen en az bir dosya türü seçin.');
            return;
        }

        const lorFileTypes = newFile.fileType.filter(typeId => isLorFileType(typeId));
        const nonLorFileTypes = newFile.fileType.filter(typeId => !isLorFileType(typeId));

        // Yeniden sırala: Önce normal dosyalar, sonra Hak Mahrumiyeti
        const reorderedFileTypes = [...nonLorFileTypes, ...lorFileTypes];

        // Birebir aynı dosya türü kombinasyonu var mı kontrolü yapılacak
        const existingFiles = formData.filesToBeWorked || [];

        // Yeni eklenen dosyanın türlerini sıralı bir şekilde string olarak birleştirelim
        const newFileTypesString = reorderedFileTypes.sort().join(',');

        // Mevcut dosyalarda aynı kombinasyon var mı kontrol edelim
        const isDuplicate = existingFiles.some(file => {
            let existingFileTypes = [];

            // API'den gelen veriyi işleme
            if (Array.isArray(file.fileType)) {
                // Yeni API formatı - obje dizisi
                if (file.fileType.length > 0 && typeof file.fileType[0] === 'object' && file.fileType[0].id) {
                    existingFileTypes = file.fileType.map(ft => ft.id);
                }
                // Eski format - ID dizisi
                else {
                    existingFileTypes = file.fileType;
                }
            }
            // Tek bir obje gelirse (eski API formatı)
            else if (file.fileType && typeof file.fileType === 'object' && !Array.isArray(file.fileType) && file.fileType.id) {
                existingFileTypes = [file.fileType.id];
            }

            // Mevcut dosyanın türlerini sıralayıp string olarak birleştirelim
            const existingFileTypesString = existingFileTypes.sort().join(',');

            // Birebir aynı kombinasyon mu?
            return existingFileTypesString === newFileTypesString;
        });

        if (isDuplicate) {
            // Dosya türü isimlerini alalım
            const fileTypeNames = reorderedFileTypes.map(id => {
                const fileType = workFileTypes.find(type => type.id === id);
                return fileType ? fileType.title : '';
            }).filter(Boolean);

            toast.warning(`Bu dosya türü kombinasyonu zaten eklenmiş: ${fileTypeNames.join(' + ')}`);
            return;
        }

        const hasLorFileType = lorFileTypes.length > 0;

        // Hak Mahrumiyeti dosyası seçildiyse kontroller
        if (hasLorFileType) {
            if (!newFile.numberOfDays || !newFile.expensePerDay) {
                toast.warning('Hak Mahrumiyeti için gün sayısı ve gün başı fiyat alanları zorunludur.');
                return;
            }

            if (formData.willLorBeWorked === "no") {
                toast.warning('Hak Mahrumiyeti çalışılmayacak olarak işaretlendiği için bu dosya türü eklenemez.');
                return;
            }
        }

        // Her dosya türü için tutar kontrolü (Hak Mahrumiyeti hariç)
        for (const typeId of nonLorFileTypes) {
            if (!newFile.fileTypeExpenses[typeId] || newFile.fileTypeExpenses[typeId].trim() === '') {
                const fileType = workFileTypes.find(type => type.id === typeId);
                const typeName = fileType ? fileType.title : 'Bilinmeyen';
                toast.warning(`${typeName} için tutar girmeniz gerekir.`);
                return;
            }
        }

        // Dosya türü başlıklarını alalım
        const fileTypeTitles = reorderedFileTypes.map(id => {
            const fileType = workFileTypes.find(type => type.id === id);
            return fileType ? fileType.title : '';
        });

        // Her dosya türü için tutarları hazırla
        const fileTypeExpenseDetails = {};
        reorderedFileTypes.forEach(typeId => {
            if (isLorFileType(typeId)) {
                // Hak Mahrumiyeti için hesaplanan tutar
                fileTypeExpenseDetails[typeId] = parseInt(newFile.numberOfDays || 0) * parseInt(newFile.expensePerDay || 0);
            } else {
                // Diğer dosya türleri için girilen tutar
                fileTypeExpenseDetails[typeId] = Number((newFile.fileTypeExpenses[typeId] || '').replace(/[^0-9]/g, '') || 0);
            }
        });

        // Yeni dosya nesnesi oluştur - Yeni API formatına uygun
        const fileToAdd = {
            fileType: reorderedFileTypes,         // Dosya türleri array olarak
            fileTypeExpenseDetails: fileTypeExpenseDetails, // Her dosya türü için ayrı tutar
            withReport: newFile.withReport,
            withFault: newFile.withFault,
            withoutReport: newFile.withoutReport,
            numberOfDays: hasLorFileType ? parseInt(newFile.numberOfDays || 0) : 0,
            expensePerDay: hasLorFileType ? parseInt(newFile.expensePerDay || 0) : 0,
            _fileTypeTitles: fileTypeTitles       // UI gösterimi için başlıklar
        };

        // Dosyaları güncelle
        const updatedFiles = [...(formData.filesToBeWorked || []), fileToAdd];
        setFormData(prevFormData => ({
            ...prevFormData,
            filesToBeWorked: updatedFiles
        }));

        // Formları temizle
        setNewFile({
            fileType: [],
            fileTypeExpenses: {},
            withReport: true,
            withFault: false,
            withoutReport: false,
            numberOfDays: '',
            expensePerDay: ''
        });
        setSelectedFileTypes([]);
    };

    return (
        <>
            {['Eksper İşlem Bekleniyor',
                'Eksper Rapor Bekleniyor',
                'Eksper Raporu Ödeme Bekleniyor',
                'Sigorta Başvuru Dilekçe Bekleniyor',
                'Tüm Form'].includes(callFormState) && (
                    <>
                        <H5>Evrak Talep Bilgileri</H5>
                        <div style={{ height: "20px" }}></div>

                        <FormGroup className="d-flex align-items-center mb-3">
                            <Label for="isThereOpenDamageFileRadio" className="me-3 text-nowrap" style={{ minWidth: '200px' }}>
                                Açık Hasar Dosyası Var Mı? {isOpenDamageFileAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <div className="d-flex">
                                <div className="radio radio-success me-3 d-flex align-items-center">
                                    <Input
                                        id="isThereOpenDamageFileTrue"
                                        type="radio"
                                        name="isThereOpenDamageFile"
                                        value="yes"
                                        checked={formData.isThereOpenDamageFile === "yes"}
                                        onChange={() =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                isThereOpenDamageFile: "yes",
                                            }))
                                        }
                                        invalid={!!errors.isThereOpenDamageFile}
                                        disabled={isOpenDamageFileAccess.disabled}
                                        required={isOpenDamageFileAccess.required}
                                    />
                                    <Label for="isThereOpenDamageFileTrue" className="ms-1 mb-0">
                                        Evet
                                    </Label>
                                </div>
                                <div className="radio radio-danger d-flex align-items-center">
                                    <Input
                                        id="isThereOpenDamageFileFalse"
                                        type="radio"
                                        name="isThereOpenDamageFile"
                                        value="no"
                                        checked={formData.isThereOpenDamageFile === "no"}
                                        onChange={() =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                isThereOpenDamageFile: "no",
                                            }))
                                        }
                                        invalid={!!errors.isThereOpenDamageFile}
                                        disabled={isOpenDamageFileAccess.disabled}
                                    />
                                    <Label for="isThereOpenDamageFileFalse" className="ms-1 mb-0">
                                        Hayır
                                    </Label>
                                </div>
                            </div>
                        </FormGroup>

                        <FormGroup className="d-flex align-items-center me-2 mt-3 flex-wrap">
                            <Label for="whoseInsurance">Başvuru Yapılacak Yer {whoseInsuranceAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="select"
                                id="whoseInsurance"
                                name="whoseInsurance"
                                value={formData.whoseInsurance}
                                onChange={handleInputChange}
                                className="form-control"
                                disabled={whoseInsuranceAccess.disabled}
                                required={whoseInsuranceAccess.required}
                            >
                                <option value="KARŞI TARAF TRAFİK SİGORTA">KARŞI TARAF TRAFİK SİGORTA</option>
                                <option value="KARŞI TARAF KASKO">KARŞI TARAF KASKO</option>
                                <option value="MÜVEKKİL TRAFİK SİGORTA">MÜVEKKİL TRAFİK SİGROTA</option>
                                <option value="MÜVEKKİL KASKO">MÜVEKKİL KASKO</option>
                            </Input>
                        </FormGroup>

                        <div style={{ height: "20px" }}></div>
                        <hr className="mt-4 mb-4" />
                        <div style={{ height: "20px" }}></div>

                        <H6>Müvekkil Sigorta Bilgileri</H6>
                        <div style={{ height: "20px" }}></div>

                        <FormGroup>
                            <Label for="ourFaultRate">Müvekkil Kusur Oranı {ourFaultRateAccess.required && <span className="text-danger">*</span>}</Label>
                            <div className="input-group">
                                <Input
                                    type="number"
                                    id="ourFaultRate"
                                    name="ourFaultRate"
                                    value={formData.ourFaultRate || ''}
                                    onChange={(e) => {
                                        let value = e.target.value.replace(/[^0-9]/g, '');
                                        if (value && parseInt(value) > 100) {
                                            value = '100';
                                        }

                                        handleInputChange({
                                            target: {
                                                name: 'ourFaultRate',
                                                value
                                            }
                                        });

                                        const counterValue = value ? (100 - parseInt(value)).toString() : '';
                                        handleInputChange({
                                            target: {
                                                name: 'counterpartyFaultRate',
                                                value: counterValue
                                            }
                                        });
                                    }}
                                    placeholder="0-100"
                                    invalid={!!errors.ourFaultRate || (formData.ourFaultRate && (parseInt(formData.ourFaultRate) > 100 || parseInt(formData.ourFaultRate) < 0))}
                                    disabled={ourFaultRateAccess.disabled}
                                    required={ourFaultRateAccess.required}
                                />
                                <div className="input-group-append">
                                    <span className="input-group-text">%</span>
                                </div>
                                <FormFeedback>
                                    {errors.ourFaultRate || 'Oran 0-100 arasında olmalıdır'}
                                </FormFeedback>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Row>
                                <Col md={3}>
                                    <Label for="trafficInsuranceProvider">Müvekkil Trafik Sigorta Sağlayıcısı {clientTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="select"
                                        id="trafficInsuranceProvider"
                                        name="trafficInsuranceProvider"
                                        value={formData.trafficInsurancePolicy?.trafficInsuranceProvider?.[0] || ''}
                                        onChange={(e) => {
                                            const providerId = e.target.value;
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                trafficInsurancePolicy: {
                                                    ...prevFormData.trafficInsurancePolicy,
                                                    trafficInsuranceProvider: providerId === "" ? [] : [providerId],
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.trafficInsuranceProvider}
                                        disabled={clientTrafficAccess.disabled}
                                        required={clientTrafficAccess.required}
                                    >
                                        <option value="">Seçiniz</option>
                                        {insuranceCompanies && insuranceCompanies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{errors.trafficInsuranceProvider}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="trafficInsuranceNumber">Müvekkil Trafik Sigorta Numarası {clientTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="text"
                                        id="trafficInsuranceNumber"
                                        name="trafficInsuranceNumber"
                                        value={formData.trafficInsurancePolicy?.trafficInsuranceNumber || ''}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                trafficInsurancePolicy: {
                                                    ...prevFormData.trafficInsurancePolicy,
                                                    trafficInsuranceNumber: value,
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.trafficInsuranceNumber}
                                        disabled={clientTrafficAccess.disabled}
                                        required={clientTrafficAccess.required}
                                    />
                                    <FormFeedback>{errors.trafficInsuranceNumber}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="trafficInsuranceStart">Müvekkil Trafik Başlangıç {clientTrafficStartAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="trafficInsuranceStart"
                                        name="trafficInsuranceStart"
                                        value={
                                            formData.trafficInsurancePolicy?.trafficInsuranceStart instanceof Date && !isNaN(formData.trafficInsurancePolicy?.trafficInsuranceStart)
                                                ? formData.trafficInsurancePolicy.trafficInsuranceStart
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                trafficInsurancePolicy: {
                                                    ...prevFormData.trafficInsurancePolicy,
                                                    trafficInsuranceStart: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.trafficInsuranceStart}
                                        disabled={clientTrafficStartAccess.disabled}
                                        required={clientTrafficStartAccess.required}
                                    />
                                    <FormFeedback>{errors.trafficInsuranceStart}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="trafficInsuranceExpiry">Müvekkil Trafik Sigorta Vade {clientTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="trafficInsuranceExpiry"
                                        name="trafficInsuranceExpiry"
                                        value={
                                            formData.trafficInsurancePolicy?.trafficInsuranceExpiry instanceof Date && !isNaN(formData.trafficInsurancePolicy?.trafficInsuranceExpiry)
                                                ? formData.trafficInsurancePolicy.trafficInsuranceExpiry
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                trafficInsurancePolicy: {
                                                    ...prevFormData.trafficInsurancePolicy,
                                                    trafficInsuranceExpiry: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.trafficInsuranceExpiry}
                                        disabled={clientTrafficAccess.disabled}
                                        required={clientTrafficAccess.required}
                                    />
                                    <FormFeedback>{errors.trafficInsuranceExpiry}</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>

                        <FormGroup>
                            <Row>
                                <Col md={3}>
                                    <Label for="cascoInsuranceProvider">Müvekkil Kasko Sağlayıcısı {clientCascoAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="select"
                                        id="cascoInsuranceProvider"
                                        name="cascoInsuranceProvider"
                                        value={formData.cascoInsurancePolicy?.cascoInsuranceProvider?.[0] || ''}
                                        onChange={(e) => {
                                            const providerId = e.target.value;
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                cascoInsurancePolicy: {
                                                    ...prevFormData.cascoInsurancePolicy,
                                                    cascoInsuranceProvider: providerId === "" ? [] : [providerId],
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.cascoInsuranceProvider}
                                        disabled={clientCascoAccess.disabled}
                                        required={clientCascoAccess.required}
                                    >
                                        <option value="">Seçiniz</option>
                                        {insuranceCompanies && insuranceCompanies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{errors.cascoInsuranceProvider}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="cascoInsuranceNumber">Müvekkil Kasko Numarası {clientCascoAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="text"
                                        id="cascoInsuranceNumber"
                                        name="cascoInsuranceNumber"
                                        value={formData.cascoInsurancePolicy?.cascoInsuranceNumber || ''}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                cascoInsurancePolicy: {
                                                    ...prevFormData.cascoInsurancePolicy,
                                                    cascoInsuranceNumber: value,
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.cascoInsuranceNumber}
                                        disabled={clientCascoAccess.disabled}
                                        required={clientCascoAccess.required}
                                    />
                                    <FormFeedback>{errors.cascoInsuranceNumber}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="cascoInsuranceStart">Müvekkil Kasko Başlangıç {clientCascoStartAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="cascoInsuranceStart"
                                        name="cascoInsuranceStart"
                                        value={
                                            formData.cascoInsurancePolicy?.cascoInsuranceStart instanceof Date && !isNaN(formData.cascoInsurancePolicy?.cascoInsuranceStart)
                                                ? formData.cascoInsurancePolicy.cascoInsuranceStart
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                cascoInsurancePolicy: {
                                                    ...prevFormData.cascoInsurancePolicy,
                                                    cascoInsuranceStart: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.cascoInsuranceStart}
                                        disabled={clientCascoStartAccess.disabled}
                                        required={clientCascoStartAccess.required}
                                    />
                                    <FormFeedback>{errors.cascoInsuranceStart}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="cascoInsuranceExpiry">Müvekkil Kasko Vade {clientCascoAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="cascoInsuranceExpiry"
                                        name="cascoInsuranceExpiry"
                                        value={
                                            formData.cascoInsurancePolicy?.cascoInsuranceExpiry instanceof Date && !isNaN(formData.cascoInsurancePolicy?.cascoInsuranceExpiry)
                                                ? formData.cascoInsurancePolicy.cascoInsuranceExpiry
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                cascoInsurancePolicy: {
                                                    ...prevFormData.cascoInsurancePolicy,
                                                    cascoInsuranceExpiry: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.cascoInsuranceExpiry}
                                        disabled={clientCascoAccess.disabled}
                                        required={clientCascoAccess.required}
                                    />
                                    <FormFeedback>{errors.cascoInsuranceExpiry}</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>

                        <div style={{ height: "20px" }}></div>
                        <H6>Karşı Taraf Sigorta Bilgileri</H6>
                        <div style={{ height: "20px" }}></div>

                        <FormGroup>
                            <Label for="counterpartyLicensePlateNumber">Karşı Taraf Plaka {counterpartyPlateAccess.required && <span className="text-danger">*</span>}</Label>
                            <Input
                                type="text"
                                id="counterpartyLicensePlateNumber"
                                name="counterpartyLicensePlateNumber"
                                value={formData.counterpartyLicensePlateNumber || ''}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                                    handleInputChange({
                                        target: {
                                            name: 'counterpartyLicensePlateNumber',
                                            value
                                        }
                                    });
                                }}
                                placeholder="34ABC123"
                                invalid={!!errors.counterpartyLicensePlateNumber}
                                disabled={counterpartyPlateAccess.disabled}
                                required={counterpartyPlateAccess.required}
                            />
                            <FormFeedback>{errors.counterpartyLicensePlateNumber}</FormFeedback>
                        </FormGroup>

                        <FormGroup>
                            <Label for="counterpartyFaultRate">Karşı Taraf Kusur Oranı {counterpartyFaultRateAccess.required && <span className="text-danger">*</span>}</Label>
                            <div className="input-group">
                                <Input
                                    type="text"
                                    id="counterpartyFaultRate"
                                    name="counterpartyFaultRate"
                                    value={formData.counterpartyFaultRate || ''}
                                    readOnly={true}
                                    placeholder="Otomatik hesaplanır"
                                    invalid={!!errors.counterpartyFaultRate}
                                    disabled={counterpartyFaultRateAccess.disabled}
                                    required={counterpartyFaultRateAccess.required}
                                />
                                <div className="input-group-append">
                                    <span className="input-group-text">%</span>
                                </div>
                                <FormFeedback>
                                    {errors.counterpartyFaultRate}
                                </FormFeedback>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Row>
                                <Col md={3}>
                                    <Label for="counterpartyTrafficInsuranceProvider">Karşı Taraf Trafik Sigorta Sağlayıcısı {cpTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="select"
                                        id="counterpartyTrafficInsuranceProvider"
                                        name="counterpartyTrafficInsuranceProvider"
                                        value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceProvider?.[0] || ''}
                                        onChange={(e) => {
                                            const providerId = e.target.value;
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyTrafficInsurancePolicy: {
                                                    ...prevFormData.counterpartyTrafficInsurancePolicy,
                                                    trafficInsuranceProvider: providerId === "" ? [] : [providerId],
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.counterpartyTrafficInsuranceProvider}
                                        disabled={cpTrafficAccess.disabled}
                                        required={cpTrafficAccess.required}
                                    >
                                        <option value="">Seçiniz</option>
                                        {insuranceCompanies && insuranceCompanies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{errors.counterpartyTrafficInsuranceProvider}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyTrafficInsuranceNumber">Karşı Taraf Trafik Sigorta Numarası {cpTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="text"
                                        id="counterpartyTrafficInsuranceNumber"
                                        name="counterpartyTrafficInsuranceNumber"
                                        value={formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceNumber || ''}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyTrafficInsurancePolicy: {
                                                    ...prevFormData.counterpartyTrafficInsurancePolicy,
                                                    trafficInsuranceNumber: value,
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.counterpartyTrafficInsuranceNumber}
                                        disabled={cpTrafficAccess.disabled}
                                        required={cpTrafficAccess.required}
                                    />
                                    <FormFeedback>{errors.counterpartyTrafficInsuranceNumber}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyTrafficInsuranceStart">Karşı Taraf Trafik Başlangıç {cpTrafficStartAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="counterpartyTrafficInsuranceStart"
                                        name="counterpartyTrafficInsuranceStart"
                                        value={
                                            formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart instanceof Date && !isNaN(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceStart)
                                                ? formData.counterpartyTrafficInsurancePolicy.trafficInsuranceStart
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyTrafficInsurancePolicy: {
                                                    ...prevFormData.counterpartyTrafficInsurancePolicy,
                                                    trafficInsuranceStart: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.counterpartyTrafficInsuranceStart}
                                        disabled={cpTrafficStartAccess.disabled}
                                        required={cpTrafficStartAccess.required}
                                    />
                                    <FormFeedback>{errors.counterpartyTrafficInsuranceStart}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyTrafficInsuranceExpiry">Karşı Taraf Trafik Sigorta Vade {cpTrafficAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="counterpartyTrafficInsuranceExpiry"
                                        name="counterpartyTrafficInsuranceExpiry"
                                        value={
                                            formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry instanceof Date && !isNaN(formData.counterpartyTrafficInsurancePolicy?.trafficInsuranceExpiry)
                                                ? formData.counterpartyTrafficInsurancePolicy.trafficInsuranceExpiry
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyTrafficInsurancePolicy: {
                                                    ...prevFormData.counterpartyTrafficInsurancePolicy,
                                                    trafficInsuranceExpiry: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.counterpartyTrafficInsuranceExpiry}
                                        disabled={cpTrafficAccess.disabled}
                                        required={cpTrafficAccess.required}
                                    />
                                    <FormFeedback>{errors.counterpartyTrafficInsuranceExpiry}</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>

                        <FormGroup>
                            <Row>
                                <Col md={3}>
                                    <Label for="counterpartyCascoInsuranceProvider">Karşı Taraf Kasko Sağlayıcısı {cpCascoAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="select"
                                        id="counterpartyCascoInsuranceProvider"
                                        name="counterpartyCascoInsuranceProvider"
                                        value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceProvider?.[0] || ''}
                                        onChange={(e) => {
                                            const providerId = e.target.value;
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyCascoInsurancePolicy: {
                                                    ...prevFormData.counterpartyCascoInsurancePolicy,
                                                    cascoInsuranceProvider: providerId === "" ? [] : [providerId],
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.counterpartyCascoInsuranceProvider}
                                        disabled={cpCascoAccess.disabled}
                                        required={cpCascoAccess.required}
                                    >
                                        <option value="">Seçiniz</option>
                                        {insuranceCompanies && insuranceCompanies.map((company) => (
                                            <option key={company.id} value={company.id}>
                                                {company.name}
                                            </option>
                                        ))}
                                    </Input>
                                    <FormFeedback>{errors.counterpartyCascoInsuranceProvider}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyCascoInsuranceNumber">Karşı Taraf Kasko Numarası {cpCascoAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="text"
                                        id="counterpartyCascoInsuranceNumber"
                                        name="counterpartyCascoInsuranceNumber"
                                        value={formData.counterpartyCascoInsurancePolicy?.cascoInsuranceNumber || ''}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyCascoInsurancePolicy: {
                                                    ...prevFormData.counterpartyCascoInsurancePolicy,
                                                    cascoInsuranceNumber: value,
                                                },
                                            }))
                                        }}
                                        invalid={!!errors.counterpartyCascoInsuranceNumber}
                                        disabled={cpCascoAccess.disabled}
                                        required={cpCascoAccess.required}
                                    />
                                    <FormFeedback>{errors.counterpartyCascoInsuranceNumber}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyCascoInsuranceStart">Karşı Taraf Kasko Başlangıç {cpCascoStartAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="counterpartyCascoInsuranceStart"
                                        name="counterpartyCascoInsuranceStart"
                                        value={
                                            formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart instanceof Date && !isNaN(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceStart)
                                                ? formData.counterpartyCascoInsurancePolicy.cascoInsuranceStart
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyCascoInsurancePolicy: {
                                                    ...prevFormData.counterpartyCascoInsurancePolicy,
                                                    cascoInsuranceStart: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.counterpartyCascoInsuranceStart}
                                        disabled={cpCascoStartAccess.disabled}
                                        required={cpCascoStartAccess.required}
                                    />
                                    <FormFeedback>{errors.counterpartyCascoInsuranceStart}</FormFeedback>
                                </Col>
                                <Col md={3}>
                                    <Label for="counterpartyCascoInsuranceExpiry">Karşı Taraf Kasko Vade {cpCascoAccess.required && <span className="text-danger">*</span>}</Label>
                                    <Input
                                        type="date"
                                        id="counterpartyCascoInsuranceExpiry"
                                        name="counterpartyCascoInsuranceExpiry"
                                        value={
                                            formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry instanceof Date && !isNaN(formData.counterpartyCascoInsurancePolicy?.cascoInsuranceExpiry)
                                                ? formData.counterpartyCascoInsurancePolicy.cascoInsuranceExpiry
                                                    .toISOString()
                                                    .substring(0, 10)
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                counterpartyCascoInsurancePolicy: {
                                                    ...prevFormData.counterpartyCascoInsurancePolicy,
                                                    cascoInsuranceExpiry: e.target.value
                                                        ? new Date(e.target.value)
                                                        : null,
                                                },
                                            }))
                                        }
                                        invalid={!!errors.counterpartyCascoInsuranceExpiry}
                                        disabled={cpCascoAccess.disabled}
                                        required={cpCascoAccess.required}
                                    />
                                    <FormFeedback>{errors.counterpartyCascoInsuranceExpiry}</FormFeedback>
                                </Col>
                            </Row>
                        </FormGroup>

                        <div style={{ height: "20px" }}></div>
                        <hr className="mt-4 mb-4" />
                        <div style={{ height: "20px" }}></div>
                    </>
                )}

            <H5>Çalışılacak Dosyalar</H5>

            {/* File upload section has been divided into two separate columns:
                1. Eksper Rapor - For expert report file uploads
                2. Sigorta Başvuru Dilekçesi - For insurance application document uploads */}

            {['Eksper İşlem Bekleniyor',
                'Tüm Form'].includes(callFormState) && (
                    <>
                        <div style={{ height: "20px" }}></div>

                        <h6 className="mb-3">Yeni Dosya Ekle</h6>

                        {(() => {
                            const hasLorFileType = newFile.fileType.some(typeId => isLorFileType(typeId));
                            const hasNonLorFileType = newFile.fileType.some(typeId => !isLorFileType(typeId));

                            return (
                                <>
                                    {formData.willLorBeWorked === "no" && (
                                        <div className="alert alert-warning mb-3">
                                            <strong>Bilgi:</strong> Hak mahrumiyeti çalışılmayacak olarak işaretlendiği için,
                                            hak mahrumiyeti ile ilgili dosyalar oluşturulamayacak ve raporlanamayacaktır.
                                        </div>
                                    )}

                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label className="form-label">Dosya Türü (Maximum 2 seçim) {fileTypeAccess.required && <span className="text-danger">*</span>}</Label>
                                                <div className="file-type-checkboxes border p-3 rounded" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                                    {workFileTypes && workFileTypes.length > 0 ?
                                                        workFileTypes
                                                            .filter(fileType => {
                                                                const isLorFileTypeCheck = fileType.title.toLowerCase().includes('hak mahrumiyeti') ||
                                                                    fileType.title.toLowerCase().includes('lor') ||
                                                                    fileType.title.toLowerCase().includes('mahrumiyeti');

                                                                return !(isLorFileTypeCheck && formData.willLorBeWorked === "no");
                                                            })
                                                            .map((fileType) => (
                                                                <div key={fileType.id} className="checkbox-item mb-2">
                                                                    <div className="checkbox checkbox-primary d-flex align-items-center">
                                                                        <Input
                                                                            type="checkbox"
                                                                            id={`fileType-${fileType.id}`}
                                                                            checked={newFile.fileType.includes(fileType.id)}
                                                                            onChange={(e) => handleFileTypeChange(fileType.id, e.target.checked)}
                                                                            disabled={fileTypeAccess.disabled}
                                                                        />
                                                                        <Label htmlFor={`fileType-${fileType.id}`} className="ms-2 mb-0">
                                                                            {fileType.title}
                                                                        </Label>
                                                                    </div>
                                                                </div>
                                                            )) :
                                                        <div className="text-center text-muted">Dosya türü bulunamadı</div>
                                                    }
                                                </div>
                                                {fileTypeAccess.required && newFile.fileType.length === 0 && <div className="text-danger small mt-1">En az bir dosya türü seçimi zorunludur.</div>}
                                            </FormGroup>
                                        </Col>

                                        {/* Dosya türü seçildikten sonra dosya özellikleri gösterilsin */}
                                        {newFile.fileType.length > 0 && (
                                            <Col md={6}>
                                                <Label className="form-label">Dosya Özellikleri</Label>
                                                <div className="d-flex flex-column">
                                                    <FormGroup check inline className="checkbox checkbox-success me-3 mb-2">
                                                        <Input
                                                            type="checkbox"
                                                            id="withReport"
                                                            checked={newFile.withReport}
                                                            onChange={() => handleCheckboxChange('withReport')}
                                                            disabled={withReportAccess.disabled}
                                                        />
                                                        <Label for="withReport" check className="text-success fw-medium">Raporlu</Label>
                                                    </FormGroup>
                                                    <FormGroup check inline className="checkbox checkbox-warning me-3 mb-2">
                                                        <Input
                                                            type="checkbox"
                                                            id="withFault"
                                                            checked={newFile.withFault}
                                                            onChange={() => handleCheckboxChange('withFault')}
                                                            disabled={withFaultAccess.disabled}
                                                        />
                                                        <Label for="withFault" check className="text-warning fw-medium">Kusurlu</Label>
                                                    </FormGroup>
                                                    <FormGroup check inline className="checkbox checkbox-danger">
                                                        <Input
                                                            type="checkbox"
                                                            id="withoutReport"
                                                            checked={newFile.withoutReport}
                                                            onChange={() => handleCheckboxChange('withoutReport')}
                                                            disabled={withoutReportAccess.disabled}
                                                        />
                                                        <Label for="withoutReport" check className="text-danger fw-medium">Raporsuz</Label>
                                                    </FormGroup>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>

                                    {/* Her dosya türü için ayrı tutar girişleri */}
                                    {newFile.fileType.length > 0 && (
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <Label className="form-label mb-3">Dosya Türü Tutarları</Label>
                                                <Row>
                                                    {newFile.fileType.map((typeId, index) => {
                                                        const fileType = workFileTypes.find(type => type.id === typeId);
                                                        const isLor = isLorFileType(typeId);
                                                        if (isLor) {
                                                            // Hak mahrumiyeti için inline görünüm: gün sayısı, gün başı fiyat ve otomatik hesaplanan tutar
                                                            const calculatedExpense = (parseInt(newFile.numberOfDays || 0) * parseInt(newFile.expensePerDay || 0));
                                                            return (
                                                                <Col md={12} key={typeId}>
                                                                    <Label className="form-label mb-3">{fileType ? fileType.title : 'Hak Mahrumiyeti'}</Label>
                                                                    <Row>
                                                                        <Col md={4}>
                                                                            <FormGroup>
                                                                                <Label for="numberOfDays" className="form-label">Gün Sayısı {numberOfDaysAccess.required && <span className="text-danger">*</span>}</Label>
                                                                                <Input
                                                                                    type="text"
                                                                                    id="numberOfDays"
                                                                                    name="numberOfDays"
                                                                                    value={newFile.numberOfDays}
                                                                                    onChange={(e) => {
                                                                                        const value = e.target.value.replace(/\D/g, '');
                                                                                        handleChange('numberOfDays', value);
                                                                                    }}
                                                                                    placeholder="0"
                                                                                    invalid={!!errors.numberOfDays}
                                                                                    className="form-control"
                                                                                    disabled={numberOfDaysAccess.disabled}
                                                                                    required={numberOfDaysAccess.required}
                                                                                />
                                                                                <FormFeedback>{errors.numberOfDays}</FormFeedback>
                                                                            </FormGroup>
                                                                        </Col>
                                                                        <Col md={4}>
                                                                            <FormGroup>
                                                                                <Label for="expensePerDay" className="form-label">Gün Başı Fiyat {expensePerDayAccess.required && <span className="text-danger">*</span>}</Label>
                                                                                <Input
                                                                                    type="text"
                                                                                    id="expensePerDay"
                                                                                    name="expensePerDay"
                                                                                    value={newFile.expensePerDay}
                                                                                    onChange={(e) => {
                                                                                        const value = e.target.value.replace(/\D/g, '');
                                                                                        handleChange('expensePerDay', value);
                                                                                    }}
                                                                                    placeholder="0"
                                                                                    invalid={!!errors.expensePerDay}
                                                                                    className="form-control"
                                                                                    disabled={expensePerDayAccess.disabled}
                                                                                    required={expensePerDayAccess.required}
                                                                                />
                                                                                <FormFeedback>{errors.expensePerDay}</FormFeedback>
                                                                            </FormGroup>
                                                                        </Col>
                                                                        <Col md={4}>
                                                                            <FormGroup>
                                                                                <Label className="form-label">Toplam Tutar (Otomatik Hesaplanan)</Label>
                                                                                <Input
                                                                                    type="text"
                                                                                    value={calculatedExpense.toLocaleString('tr-TR')}
                                                                                    disabled={true}
                                                                                    className="form-control text-dark fw-bold"
                                                                                    style={{ backgroundColor: '#e9ecef' }}
                                                                                />
                                                                            </FormGroup>
                                                                        </Col>
                                                                    </Row>
                                                                </Col>
                                                            );
                                                        } else {
                                                            // Normal dosya türleri için manuel tutar girişi
                                                            return (
                                                                <Col md={6} key={typeId}>
                                                                    <FormGroup>
                                                                        <Label className="form-label">
                                                                            {fileType ? fileType.title : 'Bilinmeyen'} Tutarı
                                                                            {fileExpenseAccess.required && <span className="text-danger">*</span>}
                                                                        </Label>
                                                                        <Input
                                                                            type="text"
                                                                            value={newFile.fileTypeExpenses[typeId] || ''}
                                                                            onChange={(e) => {
                                                                                const value = e.target.value.replace(/\D/g, '');
                                                                                handleFileTypeExpenseChange(typeId, value);
                                                                            }}
                                                                            placeholder="0"
                                                                            className="form-control"
                                                                            disabled={fileExpenseAccess.disabled}
                                                                            required={fileExpenseAccess.required}
                                                                        />
                                                                    </FormGroup>
                                                                </Col>
                                                            );
                                                        }
                                                    })}
                                                </Row>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row className="mt-4 mb-4">
                                        <Col md={12} className="text-end">
                                            <Button
                                                color="primary"
                                                onClick={handleAdd}
                                                disabled={newFile.fileType.length === 0 || addWorkFileAccess.disabled}
                                            >
                                                Dosya Ekle
                                            </Button>
                                        </Col>
                                    </Row>

                                    <hr />
                                </>
                            );
                        })()}
                    </>
                )}

            <div className="mt-4">
                <h6 className="mb-3">Eklenen Dosyalar</h6>

                {formData.filesToBeWorked && formData.filesToBeWorked.length > 0 ? (
                    <div className="table-responsive">
                        <table className="table table-bordered table-striped">
                            <thead className="bg-light">
                                <tr>
                                    {['Eksper İşlem Bekleniyor', 'Tüm Form'].includes(callFormState) && (
                                        <th style={{ width: '80px' }}>İşlem</th>
                                    )}
                                    <th style={{ minWidth: '100px' }}>Dosya No</th>
                                    <th style={{ minWidth: '150px' }}>Dosya Türü</th>
                                    <th style={{ minWidth: '150px' }}>Tutar Bilgileri</th>
                                    <th style={{ minWidth: '120px' }}>Özellikler</th>
                                    {['Eksper Rapor Bekleniyor', 'Eksper Raporu Ödeme Bekleniyor'].includes(callFormState) && (
                                        <th style={{ minWidth: '300px' }}>Eksper Rapor</th>
                                    )}
                                    {callFormState === 'Sigorta Başvuru Dilekçe Bekleniyor' && (
                                        <th style={{ minWidth: '300px' }}>Sigorta Başvuru Dilekçesi</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    // Yeni veri yapısına göre dosyaları göster
                                    // Her entry bir kaza dosyasını temsil ediyor ve kendi filesToBeWorked dizisi var
                                    let filesToShow = formData.filesToBeWorked;

                                    if (callFormState !== 'Eksper İşlem Bekleniyor' && callFormState !== 'Tüm Form') {
                                        // Hak Mahrumiyeti filtrelemesi gerekirse burada yapılabilir
                                        filesToShow = formData.filesToBeWorked.filter((accidentFileEntry) => {
                                            // AccidentFile entry'sinin filesToBeWorked'ündeki dosya türlerini kontrol et
                                            if (!accidentFileEntry.filesToBeWorked || accidentFileEntry.filesToBeWorked.length === 0) {
                                                return true; // Boş olanları göster
                                            }
                                            
                                            // Tüm filesToBeWorked objelerinin sadece LOR olup olmadığını kontrol et
                                            const allFilesAreLorOnly = accidentFileEntry.filesToBeWorked.every(file => {
                                                const titles = file._fileTypeTitles || [];
                                                return titles.length > 0 && titles.every(title => 
                                                    title.toLowerCase().includes('hak mahrumiyeti') ||
                                                    title.toLowerCase().includes('lor') ||
                                                    title.toLowerCase().includes('mahrumiyeti')
                                                );
                                            });
                                            
                                            return !allFilesAreLorOnly; // Sadece LOR olanları gizle
                                        });
                                    }

                                    return filesToShow.map((accidentFileEntry, index) => {
                                        const fileNumber = accidentFileEntry.fileNumber || (accidentFileEntry.accidentFileId ? 
                                            (() => {
                                                const accidentFile = formData.accidentFiles?.find(af => af.id === accidentFileEntry.accidentFileId);
                                                return accidentFile ? accidentFile.fileNumber : accidentFileEntry.accidentFileId;
                                            })() : 'Yeni'
                                        );

                                        // Eski format uyumluluğu - handleAdd'den gelen dosyalar için
                                        let actualEntry = accidentFileEntry;
                                        let allFileTypeTitles = [];
                                        
                                        // Eğer bu eski format dosya ise (handleAdd'den gelen)
                                        if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                                            // Eski format dosyayı tabloda gösterebilmek için dönüştür
                                            allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
                                            actualEntry = {
                                                ...accidentFileEntry,
                                                filesToBeWorked: [accidentFileEntry] // Kendisini filesToBeWorked içine koy
                                            };
                                        } else {
                                            // Yeni format (API'den gelen) - normal işleme
                                            actualEntry.filesToBeWorked?.forEach(file => {
                                                if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                                                    allFileTypeTitles.push(...file._fileTypeTitles);
                                                }
                                            });
                                        }

                                        const lorTitles = allFileTypeTitles.filter(title => {
                                            return title.toLowerCase().includes('hak mahrumiyeti') ||
                                                title.toLowerCase().includes('lor') ||
                                                title.toLowerCase().includes('mahrumiyeti');
                                        });

                                        const nonLorTitles = allFileTypeTitles.filter(title => {
                                            return !(title.toLowerCase().includes('hak mahrumiyeti') ||
                                                title.toLowerCase().includes('lor') ||
                                                title.toLowerCase().includes('mahrumiyeti'));
                                        });

                                        const reorderedTitles = [...nonLorTitles, ...lorTitles];

                                        return (
                                            <tr key={`accident-file-${index}`}>
                                                {['Eksper İşlem Bekleniyor', 'Tüm Form'].includes(callFormState) && (
                                                    <td>
                                                        <Button
                                                            color="danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveWorkFile(index)}
                                                            disabled={removeWorkFileAccess.disabled}
                                                        >
                                                            Sil
                                                        </Button>
                                                    </td>
                                                )}
                                                <td className="text-center">
                                                    <span className={`badge ${actualEntry.accidentFileId ? 'bg-primary' : 'bg-success'} rounded-pill`}>
                                                        {fileNumber}
                                                    </span>
                                                </td>
                                                <td>
                                                    {reorderedTitles.length > 0 ?
                                                        reorderedTitles.join(', ') :
                                                        'N/A'}
                                                </td>
                                                <td>
                                                    {/* Her filesToBeWorked objesi için ayrı tutar gösterimi */}
                                                    <div>
                                                        {actualEntry.filesToBeWorked?.map((file, fileIndex) => {
                                                            const titles = file._fileTypeTitles || [];
                                                            return titles.map((title, titleIndex) => {
                                                                const isLorType = title.toLowerCase().includes('hak mahrumiyeti') ||
                                                                                 title.toLowerCase().includes('lor') ||
                                                                                 title.toLowerCase().includes('mahrumiyeti');
                                                                
                                                                const uniqueKey = `${fileIndex}-${titleIndex}`;
                                                                const isFirstFile = fileIndex === 0;
                                                                
                                                                // Her dosya türü için ayrı tutar hesapla
                                                                let fileTypeAmount = 0;
                                                                if (isLorType) {
                                                                    fileTypeAmount = (file.numberOfDays || 0) * (file.expensePerDay || 0);
                                                                } else {
                                                                    // Eski format için fileTypeExpenseDetails içinden bu dosya türünün tutarını al
                                                                    if (file.fileTypeExpenseDetails) {
                                                                        const fileTypeId = file.fileType?.find(id => {
                                                                            const workFileType = workFileTypes.find(type => type.id === id);
                                                                            return workFileType && workFileType.title === title;
                                                                        });
                                                                        fileTypeAmount = file.fileTypeExpenseDetails[fileTypeId] || 0;
                                                                    } else {
                                                                        // Yeni format için direkt file expense
                                                                        fileTypeAmount = file.fileExpense || 0;
                                                                    }
                                                                }
                                                                
                                                                return (
                                                                    <div key={uniqueKey} className={!isFirstFile ? "mt-3 pt-2 border-top" : ""}>
                                                                        <div className="fw-bold">{title}:</div>
                                                                        {isLorType ? (
                                                                            <>
                                                                                <div>Gün: {file.numberOfDays || 0}</div>
                                                                                <div>Gün Başı: {(file.expensePerDay || 0).toLocaleString('tr-TR')}</div>
                                                                                <div>Toplam: {fileTypeAmount.toLocaleString('tr-TR')}</div>
                                                                            </>
                                                                        ) : (
                                                                            <div>Tutar: {fileTypeAmount.toLocaleString('tr-TR')}</div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            });
                                                        }).flat()}
                                                        {(!actualEntry.filesToBeWorked || actualEntry.filesToBeWorked.length === 0) && (
                                                            <div className="text-muted">Dosya türü belirtilmemiş</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    {actualEntry.withReport && (
                                                        <span className="badge bg-success rounded-pill ms-2 mt-2" style={{ minWidth: '75px', display: 'inline-block', textAlign: 'center' }}>Raporlu</span>
                                                    )}
                                                    {actualEntry.withFault && (
                                                        <span className="badge bg-warning rounded-pill ms-2 mt-2" style={{ minWidth: '75px', display: 'inline-block', textAlign: 'center' }}>Kusurlu</span>
                                                    )}
                                                    {actualEntry.withoutReport && (
                                                        <span className="badge bg-info rounded-pill ms-2 mt-2" style={{ minWidth: '75px', display: 'inline-block', textAlign: 'center' }}>Raporsuz</span>
                                                    )}
                                                </td>
                                                {['Eksper Rapor Bekleniyor', 'Eksper Raporu Ödeme Bekleniyor'].includes(callFormState) && (
                                                    <td>
                                                        <div className="d-flex flex-column gap-2">
                                                            <FilesComponent
                                                                key={`expert-report-${actualEntry.accidentFileId || index}`}
                                                                onFilesChange={(files) => handleExpertReportFilesChange(actualEntry.accidentFileId || index, files)}
                                                                multiple={false}
                                                                callFormReference={false}
                                                                referenceId={actualEntry.accidentFileId || index}
                                                                typeFilter={['EKSPER RAPORU']}
                                                                title=""
                                                                canEdit={canEdit}
                                                            />
                                                        </div>
                                                    </td>
                                                )}
                                                {callFormState === 'Sigorta Başvuru Dilekçe Bekleniyor' && (
                                                    <td>
                                                        <div className="d-flex flex-column gap-2">
                                                            <FilesComponent
                                                                key={`insurance-application-${actualEntry.accidentFileId || index}`}
                                                                onFilesChange={(files) => handleInsuranceApplicationFilesChange(actualEntry.accidentFileId || index, files)}
                                                                multiple={false}
                                                                callFormReference={false}
                                                                referenceId={actualEntry.accidentFileId || index}
                                                                typeFilter={['SİGORTA BAŞVURU DİLEKÇESİ']}
                                                                title=""
                                                                canEdit={canEdit}
                                                            />
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-muted">
                        Henüz çalışılacak dosya eklenmedi.
                    </p>
                )}
            </div>
        </>
    );
};

export default ExpertForm;
